// UI strings of the reworked Kääntäjä (Axel 2026-10-22, docs/next/rework-kaantaja-clearer-and-easier-to-use.md):
// the detected-language direction row, the field's hint, the recent translations under the empty
// field, Lisäasetukset (Kirje, Sävy, one live example), Kopioi, "Ei oikein?" and its three choices,
// the short confidence reason with "Miksi?", the you / tone chips, the doubtful words, the sound
// line and the word sheet's arrows. Finnish per docs/FINNISH-STYLE.md; nothing is truncated.
import { defineArea } from '../area';

export const trReworkFi = {
  // ---- the direction row ----
  detecting: 'Tunnistaa kielen',
  detected: 'tunnistettu',
  sourceMenu: 'Kieli, jolla kirjoitit',
  targetMenu: 'Käännöksen kieli',
  /** The source menu's first row: let the app read the language again. */
  autoDetect: 'Tunnista itse',
  dirAria: (from: string, to: string, detected: boolean) => `Käännössuunta: ${from}${detected ? ' (tunnistettu)' : ''} → ${to}`,
  // ---- the field ----
  placeholder: 'Kirjoita tai liitä valyriaa, suomea tai englantia',
  inputAria: 'Käännettävä teksti',
  recent: 'Äskeiset',
  allTranslations: 'Kaikki käännökset',
  // ---- Lisäasetukset ----
  moreLink: 'Lisäasetukset: kirje, sävy',
  moreTitle: 'Lisäasetukset',
  letter: 'Kirje',
  letterHint: 'Käännä rivi riviltä; tervehdys ja allekirjoitus pysyvät omilla riveillään.',
  tone: 'Sävy',
  toneHint: 'Kun puhuttelet jotakuta.',
  example: 'Esimerkki',
  exampleLoading: 'Käännetään…',
  // ---- the answer ----
  copy: 'Kopioi',
  copied: 'Kopioitu',
  copyFailed: 'Kopiointi ei onnistunut',
  wordByWord: 'Sana sanalta',
  why: 'Miksi?',
  back: (text: string) => `Takaisin: ”${text}”`,
  /** English "you": the chip by the answer (the English word itself is set in code, lang="en"). */
  youSwitch: (fi: string) => `vaihda: ${fi}`,
  youFi: { sg: 'sinä', pl: 'te' } as Record<'sg' | 'pl', string>,
  /** The register chip by an answer that addresses someone. */
  toneIs: (tone: string) => `sävy: ${tone.toLocaleLowerCase('fi')}`,
  play: 'Kuuntele Petersonin ääntämänä',
  syllablesAria: (s: string) => `Tavut ja paino: ${s}`,
  // ---- the doubtful words ----
  doubtful: 'Epävarmat sanat',
  // ---- Ei oikein? ----
  notRight: 'Ei oikein?',
  writeRight: 'Kirjoitan oikean käännöksen',
  writeRightHint: 'Menee tarkistukseen; hyväksyttynä kääntäjä käyttää sitä.',
  markWord: 'Merkitsen väärän sanan',
  markWordHint: 'Napauta sanaa, joka meni väärin. Käännös päivittyy heti.',
  justFlag: 'Merkitse vain tarkistettavaksi',
  justFlagHint: 'Otetaan esiin seuraavassa tarkistussessiossa.',
  addNote: 'Lisää huomautus',
  // ---- the word sheet ----
  prevWord: 'Edellinen sana',
  nextWord: 'Seuraava sana',
};

export const trReworkEn: typeof trReworkFi = {
  detecting: 'Detecting the language',
  detected: 'detected',
  sourceMenu: 'Language you wrote in',
  targetMenu: 'Translate into',
  autoDetect: 'Detect it',
  dirAria: (from, to, detected) => `Direction: ${from}${detected ? ' (detected)' : ''} → ${to}`,
  placeholder: 'Type or paste Valyrian, Finnish or English',
  inputAria: 'Text to translate',
  recent: 'Recent',
  allTranslations: 'All translations',
  moreLink: 'More settings: letter, tone',
  moreTitle: 'More settings',
  letter: 'Letter',
  letterHint: 'Translate line by line; the greeting and the signature stay on lines of their own.',
  tone: 'Tone',
  toneHint: 'When you address someone.',
  example: 'Example',
  exampleLoading: 'Translating…',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Could not copy',
  wordByWord: 'Word by word',
  why: 'Why?',
  back: (text) => `Back: “${text}”`,
  youSwitch: (fi) => `change: ${fi}`,
  youFi: { sg: 'one person', pl: 'several' },
  toneIs: (tone) => `tone: ${tone.toLowerCase()}`,
  play: 'Listen to Peterson say it',
  syllablesAria: (s) => `Syllables and stress: ${s}`,
  doubtful: 'Unsure words',
  notRight: 'Not right?',
  writeRight: 'I’ll write the right translation',
  writeRightHint: 'It goes to review; once approved the translator uses it.',
  markWord: 'I’ll mark the wrong word',
  markWordHint: 'Tap the word that went wrong. The translation updates at once.',
  justFlag: 'Just mark it for review',
  justFlagHint: 'It comes up in the next review session.',
  addNote: 'Add a note',
  prevWord: 'Previous word',
  nextWord: 'Next word',
};

// Messages.trRework (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('trRework', trReworkFi, trReworkEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof trReworkFi> {}
}
